import React from 'react';

const CreditAnalytics = ({ analytics, colorTheme }) => {
  if (!analytics) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        Loading analytics...
      </div>
    );
  }

  const getThemeClasses = () => {
    if (colorTheme === 'purple') {
      return {
        gradient: 'from-purple-500 to-pink-500',
        bg: 'bg-purple-100 dark:bg-purple-900',
        text: 'text-purple-600 dark:text-purple-400',
        icon: 'bg-purple-500'
      };
    } else if (colorTheme === 'blue') {
      return {
        gradient: 'from-blue-500 to-sky-500',
        bg: 'bg-blue-100 dark:bg-blue-900',
        text: 'text-blue-600 dark:text-blue-400',
        icon: 'bg-blue-500'
      };
    }
    return {
      gradient: 'from-green-500 to-emerald-500',
      bg: 'bg-green-100 dark:bg-green-900',
      text: 'text-green-600 dark:text-green-400',
      icon: 'bg-green-500'
    };
  };

  const theme = getThemeClasses();

  const stats = [
    {
      title: 'Total Revenue',
      value: `$${analytics.total_revenue_usd.toFixed(2)}`,
      icon: '💰',
      description: 'Lifetime earnings',
      color: 'bg-gradient-to-br from-green-500 to-emerald-500'
    },
    {
      title: 'Total Users',
      value: analytics.total_users.toLocaleString(),
      icon: '👥',
      description: 'Registered users',
      color: 'bg-gradient-to-br from-blue-500 to-sky-500'
    },
    {
      title: 'Power Users',
      value: analytics.power_users.toLocaleString(),
      icon: '⚡',
      description: 'Exempt from charges',
      color: 'bg-gradient-to-br from-yellow-500 to-orange-500'
    },
    {
      title: 'Credits Purchased',
      value: analytics.total_credits_purchased.toLocaleString(),
      icon: '💳',
      description: 'Total credits sold',
      color: `bg-gradient-to-br ${theme.gradient}`
    },
    {
      title: 'Credits Spent',
      value: analytics.total_credits_spent.toLocaleString(),
      icon: '📊',
      description: 'Total credits used',
      color: 'bg-gradient-to-br from-purple-500 to-pink-500'
    },
    {
      title: 'Active Users',
      value: analytics.active_users_with_credits.toLocaleString(),
      icon: '✅',
      description: 'Users with credits',
      color: 'bg-gradient-to-br from-teal-500 to-cyan-500'
    },
    {
      title: 'Out of Credits',
      value: analytics.users_out_of_credits.toLocaleString(),
      icon: '⚠️',
      description: 'Need to purchase',
      color: 'bg-gradient-to-br from-red-500 to-rose-500'
    },
    {
      title: 'Avg Credits/User',
      value: analytics.average_credits_per_user.toFixed(1),
      icon: '📈',
      description: 'Average purchased',
      color: 'bg-gradient-to-br from-indigo-500 to-purple-500'
    }
  ];

  const utilizationRate = analytics.total_credits_purchased > 0
    ? ((analytics.total_credits_spent / analytics.total_credits_purchased) * 100).toFixed(1)
    : 0;

  const revenuePerUser = analytics.total_users > 0
    ? (analytics.total_revenue_usd / analytics.total_users).toFixed(2)
    : 0;

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Credit System Analytics</h2>
        <p className="text-gray-600 dark:text-gray-400">Real-time insights into credit usage and revenue</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all hover:scale-105"
          >
            <div className={`${stat.color} p-4 text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium opacity-90">{stat.title}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className="text-4xl opacity-80">{stat.icon}</div>
              </div>
            </div>
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700">
              <p className="text-xs text-gray-600 dark:text-gray-400">{stat.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Utilization Rate */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <span className="mr-2">📊</span> Credit Utilization Rate
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Credits Used</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{utilizationRate}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  className={`bg-gradient-to-r ${theme.gradient} h-3 rounded-full transition-all`}
                  style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                ></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Purchased</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {analytics.total_credits_purchased.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {(analytics.total_credits_purchased - analytics.total_credits_spent).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Insights */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <span className="mr-2">💵</span> Revenue Insights
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-green-50 dark:bg-green-900 dark:bg-opacity-20 rounded-lg">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Revenue per User</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">${revenuePerUser}</p>
              </div>
              <div className="text-3xl">💎</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">Paying Users</p>
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {analytics.total_users - analytics.users_out_of_credits - analytics.power_users}
                </p>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900 dark:bg-opacity-20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">Conversion</p>
                <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                  {analytics.total_users > 0 
                    ? ((analytics.total_users - analytics.users_out_of_credits - analytics.power_users) / analytics.total_users * 100).toFixed(1)
                    : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <span className="mr-2">👥</span> User Distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900 dark:to-emerald-900 dark:bg-opacity-20 rounded-lg">
            <div className="text-3xl mb-2">✅</div>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {analytics.active_users_with_credits}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">With Credits</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {analytics.total_users > 0 ? ((analytics.active_users_with_credits / analytics.total_users) * 100).toFixed(0) : 0}%
            </p>
          </div>
          
          <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900 dark:to-orange-900 dark:bg-opacity-20 rounded-lg">
            <div className="text-3xl mb-2">⚡</div>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {analytics.power_users}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Power Users</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {analytics.total_users > 0 ? ((analytics.power_users / analytics.total_users) * 100).toFixed(0) : 0}%
            </p>
          </div>
          
          <div className="text-center p-4 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900 dark:to-rose-900 dark:bg-opacity-20 rounded-lg">
            <div className="text-3xl mb-2">⚠️</div>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {analytics.users_out_of_credits}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Out of Credits</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {analytics.total_users > 0 ? ((analytics.users_out_of_credits / analytics.total_users) * 100).toFixed(0) : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditAnalytics;
